"use client";
import { useEffect } from "react";
import { motion, stagger, useAnimate } from "framer-motion";
import { cn } from "@/lib/utils";

export const TextGenerateEffect = ({
  words,
  className,
  wordClassName,
  filter = true,
  duration = 0.5,
  staggerDelay = 0.2,
}: {
  words: string;
  className?: string;
  wordClassName?: string;
  filter?: boolean;
  duration?: number;
  staggerDelay?: number;
}) => {
  const [scope, animate] = useAnimate();
  const sanitizedWords = words
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/\r?\n/g, " ")
    .replace(/[\u2028\u2029\u000b\u000c\u0085]/g, " ")
    .replace(/\\[nrt]/g, " ")
    .replace(/([。？！；，、])/g, "$1 ")
    .replace(/([\u4e00-\u9fff])([A-Za-z0-9])/g, "$1 $2")
    .replace(/([A-Za-z0-9])([\u4e00-\u9fff])/g, "$1 $2")
    .replace(/\\+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const rawWordsArray =
    sanitizedWords.length > 0 ? sanitizedWords.split(" ") : [];
  const connectors = new Set(["比如", "例如", "比如说"]);
  const segments: { text: string; trailing: " " | "\u00A0" | "" }[] = [];

  for (let i = 0; i < rawWordsArray.length; i += 1) {
    const currentWord = rawWordsArray[i];
    if (!currentWord) {
      continue;
    }

    const nextWord = rawWordsArray[i + 1];
    let trailing: " " | "\u00A0" | "" = "";

    if (typeof nextWord === "string" && nextWord.length > 0) {
      trailing = " ";

      if (
        /[。？！；，、\u2026~]$/.test(currentWord) &&
        connectors.has(nextWord)
      ) {
        trailing = "\u00A0";
      } else if (
        connectors.has(currentWord) &&
        /^[A-Za-z0-9]/.test(nextWord)
      ) {
        trailing = "\u00A0";
      }
    }

    segments.push({ text: currentWord, trailing });
  }

  useEffect(() => {
    if (segments.length === 0) {
      return;
    }

    animate(
      "span",
      {
        opacity: 1,
        filter: filter ? "blur(0px)" : "none",
      },
      {
        duration: duration ?? 0.5,
        delay: stagger(staggerDelay),
      }
    );
  }, [animate, filter, duration, staggerDelay, segments.length]);

  return (
    <motion.div ref={scope} className={className}>
      {segments.map((segment, idx) => (
        <motion.span
          key={`${segment.text}-${idx}`}
          className={cn("opacity-0", wordClassName)}
          style={{
            filter: filter ? "blur(10px)" : "none",
            display: "inline",
          }}
        >
          {segment.text}
          {segment.trailing === " " ? " " : null}
          {segment.trailing === "\u00A0" ? "\u00A0" : null}
        </motion.span>
      ))}
    </motion.div>
  );
};
